import React, { useState, useEffect } from 'react';
import { Save, X, Calendar, BookOpen, Plus, Clock, FileText, Upload, Edit, Trash2 } from 'lucide-react';

// Service methods matching all backend endpoints
const studentService = {
  // Student endpoints
  updateStudent: async (studentId, studentData) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/students/me/${studentId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(studentData)
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to update student');
    }
    return response.json();
  },

  // Lesson endpoints
  getLessonsByStudent: async (studentId) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/lessons/student/${studentId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to fetch student lessons');
    }
    return response.json();
  },

  getLesson: async (lessonId) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/lessons/${lessonId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to fetch lesson');
    }
    return response.json();
  },

  updateLesson: async (lessonId, lessonData) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/lessons/${lessonId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(lessonData)
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to update lesson');
    }
    return response.json();
  },

  createLesson: async (lessonData) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/lessons/me`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(lessonData)
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to create lesson');
    }
    return response.json();
  },

  deleteLesson: async (lessonId) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/lessons/${lessonId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to delete lesson');
    }
    return response.ok;
  },

  // PDF endpoints
  getLessonPdf: async (lessonId) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/lessons/${lessonId}/pdf`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch lesson PDF');
    }
    return response.blob();
  },

  uploadLessonPdf: async (lessonId, pdfFile) => {
    const token = localStorage.getItem('auth_token');
    const formData = new FormData();
    formData.append('data', pdfFile);

    const response = await fetch(`${process.env.REACT_APP_API_URL}/lessons/${lessonId}/pdf`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    if (!response.ok) {
      throw new Error('Failed to upload PDF');
    }
    return response.ok;
  },

  // AI Lesson Generation
  generateAILesson: async (prompt) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/lessons/openAi`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ prompt })
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to generate lesson');
    }
    return response.blob();
  },

  // Homework endpoints
  getHomeworkByStudent: async (studentId) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/homework/student/${studentId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to fetch student homework');
    }
    return response.json();
  },

  createHomework: async (homeworkData) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/homework/me`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(homeworkData)
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to create homework');
    }
    return response.json();
  },

  updateHomework: async (homeworkId, homeworkData) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/homework/${homeworkId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(homeworkData)
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to update homework');
    }
    return response.json();
  },

  deleteHomework: async (homeworkId) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/homework/${homeworkId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to delete homework');
    }
    return response.ok;
  },

  getHomeworkPdf: async (homeworkId) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/homework/${homeworkId}/pdf`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch homework PDF');
    }
    return response.blob();
  },

  uploadHomeworkPdf: async (homeworkId, pdfFile) => {
    const token = localStorage.getItem('auth_token');
    const formData = new FormData();
    formData.append('data', pdfFile);

    const response = await fetch(`${process.env.REACT_APP_API_URL}/homework/${homeworkId}/pdf`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    if (!response.ok) {
      throw new Error('Failed to upload homework PDF');
    }
    return response.ok;
  }
};

// Lesson PDF Viewer Component
const LessonPdfViewer = ({ lessonId, onClose }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const fetchPdf = async () => {
    try {
      setLoading(true);
      setError(null);
      const blob = await studentService.getLessonPdf(lessonId);
      
      // Revoke previous URL if exists
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
      
      const newUrl = URL.createObjectURL(blob);
      setPdfUrl(newUrl);
    } catch (err) {
      setError(err.message || 'Failed to load PDF');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPdf();
    
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [lessonId, refreshTrigger]);

  const handleFileChange = (e) => {
    setPdfFile(e.target.files[0]);
    setError(null);
  };

  const handleUpload = async () => {
    if (!pdfFile) return;
    
    try {
      setUploading(true);
      setError(null);
      await studentService.uploadLessonPdf(lessonId, pdfFile);
      setPdfFile(null);
      setRefreshTrigger(prev => prev + 1); // Trigger refresh
    } catch (err) {
      setError(err.message || 'Failed to upload PDF');
    } finally {
      setUploading(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl h-[85vh] bg-white shadow-2xl rounded-lg overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <h3 className="text-xl font-semibold text-gray-800 flex items-center">
            <FileText className="w-5 h-5 mr-2 text-blue-600" />
            Lesson Material PDF
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-200 transition-colors text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-4">
              <div className="text-red-500 mb-4">{error}</div>
              <button
                onClick={handleRetry}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Retry Loading PDF
              </button>
            </div>
          ) : (
            <iframe 
              src={pdfUrl}
              className="w-full h-full min-h-[60vh]"
              title="Lesson PDF"
            />
          )}
        </div>
        
        <div className="p-4 border-t bg-gray-50 flex flex-col space-y-3">
          <div className="flex items-center space-x-3">
            <input
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              className="text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
              key={pdfFile ? 'file-selected' : 'no-file'}
            />
            <button
              onClick={handleUpload}
              disabled={!pdfFile || uploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 flex items-center"
            >
              {uploading ? 'Uploading...' : 'Upload PDF'}
              {!uploading && <Upload className="w-4 h-4 ml-2" />}
            </button>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors self-end"
          >
            Close Viewer
          </button>
        </div>
      </div>
    </div>
  );
};

// Homework PDF Viewer Component
const HomeworkPdfViewer = ({ homeworkId, onClose }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const fetchPdf = async () => {
    try {
      setLoading(true);
      setError(null);
      const blob = await studentService.getHomeworkPdf(homeworkId);
      
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
      
      const newUrl = URL.createObjectURL(blob);
      setPdfUrl(newUrl);
    } catch (err) {
      setError(err.message || 'Failed to load homework PDF');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPdf();
    
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [homeworkId, refreshTrigger]);

  const handleFileChange = (e) => {
    setPdfFile(e.target.files[0]);
    setError(null);
  };

  const handleUpload = async () => {
    if (!pdfFile) return;
    
    try {
      setUploading(true);
      setError(null);
      await studentService.uploadHomeworkPdf(homeworkId, pdfFile);
      setPdfFile(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      setError(err.message || 'Failed to upload homework PDF');
    } finally {
      setUploading(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl h-[85vh] bg-white shadow-2xl rounded-lg overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <h3 className="text-xl font-semibold text-gray-800 flex items-center">
            <FileText className="w-5 h-5 mr-2 text-blue-600" />
            Homework Assignment PDF
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-200 transition-colors text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-4">
              <div className="text-red-500 mb-4">{error}</div>
              <button
                onClick={handleRetry}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Retry Loading PDF
              </button>
            </div>
          ) : (
            <iframe 
              src={pdfUrl}
              className="w-full h-full min-h-[60vh]"
              title="Homework PDF"
            />
          )}
        </div>
        
        <div className="p-4 border-t bg-gray-50 flex flex-col space-y-3">
          <div className="flex items-center space-x-3">
            <input
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              className="text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
              key={pdfFile ? 'file-selected' : 'no-file'}
            />
            <button
              onClick={handleUpload}
              disabled={!pdfFile || uploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 flex items-center"
            >
              {uploading ? 'Uploading...' : 'Upload PDF'}
              {!uploading && <Upload className="w-4 h-4 ml-2" />}
            </button>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors self-end"
          >
            Close Viewer
          </button>
        </div>
      </div>
    </div>
  );
};

const StudentDetailPanel = ({ student, onClose, onSave }) => {
  const [openPdf, setOpenPdf] = useState(false);
  const [curLesson, setCurLesson] = useState(null);
  const [pdfViewerKey, setPdfViewerKey] = useState(0);
  const [editedStudent, setEditedStudent] = useState({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    tutor: { id: '' },
    notes: []
  });
  const [lessons, setLessons] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [noteContent, setNoteContent] = useState('');
  const [newLesson, setNewLesson] = useState({
    studentId: '',
    tutorId: '',
    subject: '',
    dateTime: '',
    duration: 60,
    status: 'Upcoming'
  });
  const [aiPrompt, setAiPrompt] = useState('');
  const [generatingAI, setGeneratingAI] = useState(false);

  // Homework state
  const [homeworks, setHomeworks] = useState([]);
  const [editingHomeworkId, setEditingHomeworkId] = useState(null);
  const [newHomework, setNewHomework] = useState({
    studentId: '',
    tutorId: '',
    subject: '',
    description: '',
    dueDate: '',
    status: 'Assigned'
  });
  const [openHomeworkPdf, setOpenHomeworkPdf] = useState(false);
  const [curHomework, setCurHomework] = useState(null);
  const [homeworkPdfViewerKey, setHomeworkPdfViewerKey] = useState(0);

  const statusOptions = ['Upcoming', 'Canceled', 'Completed & Shared'];
  const homeworkStatusOptions = ['Assigned', 'In Progress', 'Completed', 'Late'];

  useEffect(() => {
    if (student) {
      setEditedStudent({
        id: student.id,
        firstName: student.firstName || '',
        lastName: student.lastName || '',
        email: student.email || '',
        tutor: student.tutor || { id: '' },
        notes: student.notes || []
      });
      
      setNoteContent(Array.isArray(student.notes) ? student.notes.join('\n') : (student.notes || ''));
      fetchStudentLessons(student.id);
      fetchStudentHomeworks(student.id);
      
      setNewLesson(prev => ({
        ...prev,
        studentId: student.id,
        tutorId: student.tutor?.id || ''
      }));

      setNewHomework(prev => ({
        ...prev,
        studentId: student.id,
        tutorId: student.tutor?.id || ''
      }));
    }
  }, [student]);

  const fetchStudentLessons = async (studentId) => {
    try {
      const lessonData = await studentService.getLessonsByStudent(studentId);
      setLessons(lessonData);
    } catch (err) {
      console.error('Error fetching student lessons:', err);
      setError('Failed to load lesson data');
    }
  };

  const fetchStudentHomeworks = async (studentId) => {
    try {
      const homeworkData = await studentService.getHomeworkByStudent(studentId);
      setHomeworks(homeworkData);
    } catch (err) {
      console.error('Error fetching student homeworks:', err);
      setError('Failed to load homework data');
    }
  };

  const handleOpenPdf = (lessonId) => {
    setCurLesson(lessonId);
    setOpenPdf(true);
    setPdfViewerKey(prev => prev + 1);
  };

  const handleOpenHomeworkPdf = (homeworkId) => {
    setCurHomework(homeworkId);
    setOpenHomeworkPdf(true);
    setHomeworkPdfViewerKey(prev => prev + 1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedStudent({
      ...editedStudent,
      [name]: value
    });
  };

  const handleNoteChange = (e) => {
    setNoteContent(e.target.value);
  };

  const handleLessonInputChange = (e, lessonId) => {
    const { name, value } = e.target;
    const processedValue = name === 'duration' ? parseInt(value, 10) || 0 : value;
    
    if (lessonId === 'new') {
      setNewLesson({
        ...newLesson,
        [name]: processedValue
      });
    } else {
      setLessons(lessons.map(lesson => 
        lesson.id === lessonId ? { ...lesson, [name]: processedValue } : lesson
      ));
    }
  };

  const handleHomeworkInputChange = (e, homeworkId) => {
    const { name, value } = e.target;
    
    if (homeworkId === 'new') {
      setNewHomework({
        ...newHomework,
        [name]: value
      });
    } else {
      setHomeworks(homeworks.map(homework => 
        homework.id === homeworkId ? { ...homework, [name]: value } : homework
      ));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const processedNotes = noteContent
        .split('\n')
        .filter(note => note.trim() !== '');
      
      const studentToUpdate = {
        ...editedStudent,
        notes: processedNotes
      };
      
      const updatedStudent = await studentService.updateStudent(editedStudent.id, studentToUpdate);
      
      if (editingLessonId) {
        const lessonToUpdate = lessons.find(l => l.id === editingLessonId);
        if (lessonToUpdate) {
          await studentService.updateLesson(editingLessonId, lessonToUpdate);
        }
      }
      
      onSave && onSave(updatedStudent);
    } catch (err) {
      console.error('Error updating student:', err);
      setError(err.message || 'Failed to update student. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveLesson = async (lessonId) => {
    try {
      if (lessonId === 'new') {
        const createdLesson = await studentService.createLesson(newLesson);
        setLessons([...lessons, createdLesson]);
        setNewLesson({
          studentId: student.id,
          tutorId: student.tutor?.id || '',
          subject: '',
          dateTime: '',
          duration: 60,
          status: 'Upcoming'
        });
      } else {
        const lessonToUpdate = lessons.find(l => l.id === lessonId);
        const updatedLesson = await studentService.updateLesson(lessonId, lessonToUpdate);
        setLessons(lessons.map(l => l.id === lessonId ? updatedLesson : l));
      }
      setEditingLessonId(null);
    } catch (err) {
      console.error('Error saving lesson:', err);
      setError(err.message || 'Failed to save lesson. Please try again.');
    }
  };

  const saveHomework = async (homeworkId) => {
    try {
      if (homeworkId === 'new') {
        const createdHomework = await studentService.createHomework(newHomework);
        setHomeworks([...homeworks, createdHomework]);
        setNewHomework({
          studentId: student.id,
          tutorId: student.tutor?.id || '',
          subject: '',
          description: '',
          dueDate: '',
          status: 'Assigned'
        });
      } else {
        const homeworkToUpdate = homeworks.find(h => h.id === homeworkId);
        const updatedHomework = await studentService.updateHomework(homeworkId, homeworkToUpdate);
        setHomeworks(homeworks.map(h => h.id === homeworkId ? updatedHomework : h));
      }
      setEditingHomeworkId(null);
    } catch (err) {
      console.error('Error saving homework:', err);
      setError(err.message || 'Failed to save homework. Please try again.');
    }
  };

  const deleteLesson = async (lessonId) => {
    try {
      await studentService.deleteLesson(lessonId);
      setLessons(lessons.filter(l => l.id !== lessonId));
    } catch (err) {
      console.error('Error deleting lesson:', err);
      setError(err.message || 'Failed to delete lesson. Please try again.');
    }
  };

  const deleteHomework = async (homeworkId) => {
    try {
      await studentService.deleteHomework(homeworkId);
      setHomeworks(homeworks.filter(h => h.id !== homeworkId));
    } catch (err) {
      console.error('Error deleting homework:', err);
      setError(err.message || 'Failed to delete homework. Please try again.');
    }
  };

  const generateAILesson = async () => {
    if (!aiPrompt.trim()) return;
    
    try {
      setGeneratingAI(true);
      setError(null);
      const pdfBlob = await studentService.generateAILesson(aiPrompt);
      
      // Create a new lesson with the AI-generated content
      const newAILesson = {
        ...newLesson,
        subject: 'AI-Generated Lesson',
        notes: ['Generated from AI prompt: ' + aiPrompt]
      };
      
      const createdLesson = await studentService.createLesson(newAILesson);
      
      // Upload the PDF
      const formData = new FormData();
      formData.append('data', new Blob([pdfBlob], { type: 'application/pdf' }));
      await studentService.uploadLessonPdf(createdLesson.id, formData);
      
      // Refresh the lessons list
      await fetchStudentLessons(student.id);
      setAiPrompt('');
    } catch (err) {
      console.error('Error generating AI lesson:', err);
      setError(err.message || 'Failed to generate AI lesson. Please try again.');
    } finally {
      setGeneratingAI(false);
    }
  };

  const startEditingLesson = (lessonId) => {
    setEditingLessonId(lessonId);
  };

  const startEditingHomework = (homeworkId) => {
    setEditingHomeworkId(homeworkId);
  };

  const cancelEditingLesson = () => {
    if (editingLessonId === 'new') {
      setNewLesson({
        studentId: student.id,
        tutorId: student.tutor?.id || '',
        subject: '',
        dateTime: '',
        duration: 60,
        status: 'Upcoming'
      });
    }
    setEditingLessonId(null);
  };

  const cancelEditingHomework = () => {
    if (editingHomeworkId === 'new') {
      setNewHomework({
        studentId: student.id,
        tutorId: student.tutor?.id || '',
        subject: '',
        description: '',
        dueDate: '',
        status: 'Assigned'
      });
    }
    setEditingHomeworkId(null);
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours} hr${hours > 1 ? 's' : ''} ${mins > 0 ? `${mins} min${mins > 1 ? 's' : ''}` : ''}`;
    }
    return `${mins} min${mins > 1 ? 's' : ''}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Canceled':
        return 'bg-red-100 text-red-800';
      case 'Upcoming':
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getHomeworkStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Late':
        return 'bg-red-100 text-red-800';
      case 'In Progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'Assigned':
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  return (
    <div className="bg-white rounded-lg p-6">
      <div className="mb-6 border-b">
        <div className="flex space-x-4">
          <button
            type="button"
            className={`px-4 py-2 ${activeTab === 'details' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('details')}
          >
            Student Details
          </button>
          <button
            type="button"
            className={`px-4 py-2 ${activeTab === 'lessons' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('lessons')}
          >
            Lessons
          </button>
          <button
            type="button"
            className={`px-4 py-2 ${activeTab === 'homework' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('homework')}
          >
            Homework
          </button>
          <button
            type="button"
            className={`px-4 py-2 ${activeTab === 'notepad' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('notepad')}
          >
            Notepad
          </button>
        </div>
      </div>

      {activeTab === 'details' && (
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Student ID</label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-md bg-gray-100"
                value={editedStudent.id}
                disabled
                readOnly
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                type="text"
                name="firstName"
                className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={editedStudent.firstName}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                type="text"
                name="lastName"
                className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={editedStudent.lastName}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={editedStudent.email}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tutor ID</label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-md bg-gray-100"
                value={editedStudent.tutor?.id || ''}
                disabled
                readOnly
              />
            </div>

            {error && (
              <div className="text-red-600 bg-red-50 p-3 rounded-md">{error}</div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border rounded-md flex items-center hover:bg-gray-100"
                disabled={isSubmitting}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md flex items-center hover:bg-blue-700"
                disabled={isSubmitting}
              >
                <Save className="w-4 h-4 mr-2" />
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      )}

      {activeTab === 'lessons' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Lesson History</h3>
            <button
              type="button"
              onClick={() => startEditingLesson('new')}
              className="px-3 py-1 bg-green-600 text-white rounded-md flex items-center hover:bg-green-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              New Lesson
            </button>
          </div>

          {/* New Lesson Form */}
          {editingLessonId === 'new' && (
            <div className="p-4 border rounded-md bg-gray-50">
              <h4 className="font-medium mb-3">Add New Lesson</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date & Time</label>
                  <input
                    type="datetime-local"
                    name="dateTime"
                    className="w-full px-3 py-2 border rounded-md"
                    value={newLesson.dateTime}
                    onChange={(e) => handleLessonInputChange(e, 'new')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    className="w-full px-3 py-2 border rounded-md"
                    value={newLesson.subject}
                    onChange={(e) => handleLessonInputChange(e, 'new')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                  <input
                    type="number"
                    name="duration"
                    min="15"
                    step="15"
                    className="w-full px-3 py-2 border rounded-md"
                    value={newLesson.duration}
                    onChange={(e) => handleLessonInputChange(e, 'new')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    name="status"
                    className="w-full px-3 py-2 border rounded-md"
                    value={newLesson.status}
                    onChange={(e) => handleLessonInputChange(e, 'new')}
                    required
                  >
                    {statusOptions.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={cancelEditingLesson}
                  className="px-3 py-1 border rounded-md hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => saveLesson('new')}
                  className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Save Lesson
                </button>
              </div>
            </div>
          )}

          {openPdf && (
            <LessonPdfViewer 
              key={pdfViewerKey}
              lessonId={curLesson} 
              onClose={() => setOpenPdf(false)} 
            />
          )}

          {lessons.length === 0 ? (
            <p className="text-gray-500 italic">No lessons found for this student.</p>
          ) : (
            <div className="space-y-4">
              {lessons.map(lesson => (
                <div key={lesson.id} className="border rounded-md p-4">
                  {editingLessonId === lesson.id ? (
                    <div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Date & Time</label>
                          <input
                            type="datetime-local"
                            name="dateTime"
                            className="w-full px-3 py-2 border rounded-md"
                            value={lesson.dateTime}
                            onChange={(e) => handleLessonInputChange(e, lesson.id)}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                          <input
                            type="text"
                            name="subject"
                            className="w-full px-3 py-2 border rounded-md"
                            value={lesson.subject}
                            onChange={(e) => handleLessonInputChange(e, lesson.id)}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                          <input
                            type="number"
                            name="duration"
                            min="15"
                            step="15"
                            className="w-full px-3 py-2 border rounded-md"
                            value={lesson.duration}
                            onChange={(e) => handleLessonInputChange(e, lesson.id)}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                          <select
                            name="status"
                            className="w-full px-3 py-2 border rounded-md"
                            value={lesson.status}
                            onChange={(e) => handleLessonInputChange(e, lesson.id)}
                            required
                          >
                            {statusOptions.map(status => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="flex justify-between space-x-3">
                        <div className="flex space-x-2">
                          <button
                              type="button"
                              onClick={() => handleOpenPdf(lesson.id)}
                              className="px-3 py-1 border rounded-md hover:bg-gray-100"
                            >
                              View PDF
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteLesson(lesson.id)}
                            className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </div>
                        <div className='flex gap-2'>
                          <button
                            type="button"
                            onClick={cancelEditingLesson}
                            className="px-3 py-1 border rounded-md hover:bg-gray-100"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => saveLesson(lesson.id)}
                            className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                          >
                            Save Changes
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex justify-between mb-2">
                        <div className="font-medium">{lesson.subject || 'Untitled Lesson'}</div>
                        <div className="flex items-center">
                          <span className={`text-xs px-2 py-1 rounded ${getStatusColor(lesson.status)}`}>
                            {lesson.status}
                          </span>
                          <button
                            type="button"
                            onClick={() => startEditingLesson(lesson.id)}
                            className="text-blue-600 hover:text-blue-800 ml-3"
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                          <span>{new Date(lesson.dateTime).toLocaleString()}</span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-2 text-gray-500" />
                          <span>{formatDuration(lesson.duration)}</span>
                        </div>
                        <div className="flex items-center">
                          <BookOpen className="w-4 h-4 mr-2 text-gray-500" />
                          <span>{lesson.subject || 'No subject specified'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="text-red-600 bg-red-50 p-3 rounded-md">{error}</div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md flex items-center hover:bg-gray-100"
            >
              <X className="w-4 h-4 mr-2" />
              Close
            </button>
          </div>
        </div>
      )}

      {activeTab === 'homework' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Homework Assignments</h3>
            <button
              type="button"
              onClick={() => startEditingHomework('new')}
              className="px-3 py-1 bg-green-600 text-white rounded-md flex items-center hover:bg-green-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              New Homework
            </button>
          </div>

          {/* New Homework Form */}
          {editingHomeworkId === 'new' && (
            <div className="p-4 border rounded-md bg-gray-50">
              <h4 className="font-medium mb-3">Create New Homework</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    className="w-full px-3 py-2 border rounded-md"
                    value={newHomework.subject}
                    onChange={(e) => handleHomeworkInputChange(e, 'new')}
                    placeholder="e.g., Mathematics, Physics, etc."
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    className="w-full px-3 py-2 border rounded-md min-h-20"
                    value={newHomework.description}
                    onChange={(e) => handleHomeworkInputChange(e, 'new')}
                    placeholder="Homework instructions and requirements..."
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    name="dueDate"
                    className="w-full px-3 py-2 border rounded-md"
                    value={newHomework.dueDate}
                    onChange={(e) => handleHomeworkInputChange(e, 'new')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    name="status"
                    className="w-full px-3 py-2 border rounded-md"
                    value={newHomework.status}
                    onChange={(e) => handleHomeworkInputChange(e, 'new')}
                    required
                  >
                    {homeworkStatusOptions.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={cancelEditingHomework}
                  className="px-3 py-1 border rounded-md hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => saveHomework('new')}
                  className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create Homework
                </button>
              </div>
            </div>
          )}

          {openHomeworkPdf && (
            <HomeworkPdfViewer 
              key={homeworkPdfViewerKey}
              homeworkId={curHomework} 
              onClose={() => setOpenHomeworkPdf(false)} 
            />
          )}

          {homeworks.length === 0 ? (
            <p className="text-gray-500 italic">No homework assignments found for this student.</p>
          ) : (
            <div className="space-y-4">
              {homeworks.map(homework => (
                <div key={homework.id} className="border rounded-md p-4">
                  {editingHomeworkId === homework.id ? (
                    <div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                          <input
                            type="text"
                            name="subject"
                            className="w-full px-3 py-2 border rounded-md"
                            value={homework.subject}
                            onChange={(e) => handleHomeworkInputChange(e, homework.id)}
                            required
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                          <textarea
                            name="description"
                            className="w-full px-3 py-2 border rounded-md min-h-20"
                            value={homework.description}
                            onChange={(e) => handleHomeworkInputChange(e, homework.id)}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                          <input
                            type="date"
                            name="dueDate"
                            className="w-full px-3 py-2 border rounded-md"
                            value={homework.dueDate}
                            onChange={(e) => handleHomeworkInputChange(e, homework.id)}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                          <select
                            name="status"
                            className="w-full px-3 py-2 border rounded-md"
                            value={homework.status}
                            onChange={(e) => handleHomeworkInputChange(e, homework.id)}
                            required
                          >
                            {homeworkStatusOptions.map(status => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                        </div>
                        {homework.score && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Score</label>
                            <input
                              type="text"
                              name="score"
                              className="w-full px-3 py-2 border rounded-md bg-gray-100"
                              value={homework.score}
                              readOnly
                              disabled
                            />
                          </div>
                        )}
                      </div>

                      <div className="flex justify-between space-x-3">
                        <div className="flex space-x-2">
                          <button
                            type="button"
                            onClick={() => handleOpenHomeworkPdf(homework.id)}
                            className="px-3 py-1 border rounded-md hover:bg-gray-100"
                          >
                            Manage PDF
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteHomework(homework.id)}
                            className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </div>
                        <div className='flex gap-2'>
                          <button
                            type="button"
                            onClick={cancelEditingHomework}
                            className="px-3 py-1 border rounded-md hover:bg-gray-100"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => saveHomework(homework.id)}
                            className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                          >
                            Save Changes
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex justify-between mb-2">
                        <div className="font-medium">{homework.subject || 'Untitled Homework'}</div>
                        <div className="flex items-center">
                          <span className={`text-xs px-2 py-1 rounded ${getHomeworkStatusColor(homework.status)}`}>
                            {homework.status}
                          </span>
                          <button
                            type="button"
                            onClick={() => startEditingHomework(homework.id)}
                            className="text-blue-600 hover:text-blue-800 ml-3 flex items-center"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm mb-3">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                          <span>Due: {formatDate(homework.dueDate)}</span>
                        </div>
                        {homework.score && (
                          <div className="flex items-center">
                            <span className="font-medium">Score: {homework.score}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="text-sm text-gray-600 mb-3">
                        {homework.description || 'No description provided.'}
                      </div>

                      <div className="flex justify-between items-center">
                        <button
                          type="button"
                          onClick={() => handleOpenHomeworkPdf(homework.id)}
                          className="px-3 py-1 border rounded-md hover:bg-gray-100 flex items-center"
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          Manage PDF
                        </button>
                        
                        <div className="text-xs text-gray-500">
                          Created: {formatDate(homework.dateTime || homework.dueDate)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="text-red-600 bg-red-50 p-3 rounded-md">{error}</div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md flex items-center hover:bg-gray-100"
            >
              <X className="w-4 h-4 mr-2" />
              Close
            </button>
          </div>
        </div>
      )}

      {activeTab === 'notepad' && (
        <div className="space-y-6">
          <div className="flex items-center mb-4">
            <FileText className="w-5 h-5 mr-2 text-gray-500" />
            <h3 className="text-lg font-medium">Student Notes</h3>
          </div>
          
          <div>
            <textarea
              className="w-full px-3 py-2 border rounded-md min-h-64 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter notes about this student here..."
              value={noteContent}
              onChange={handleNoteChange}
            ></textarea>
            <p className="text-sm text-gray-500 mt-2">
              Each line will be saved as a separate note. Empty lines will be removed.
            </p>
          </div>
          
          {error && (
            <div className="text-red-600 bg-red-50 p-3 rounded-md">{error}</div>
          )}
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md flex items-center hover:bg-gray-100"
              disabled={isSubmitting}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="px-4 py-2 bg-blue-600 text-white rounded-md flex items-center hover:bg-blue-700"
              disabled={isSubmitting}
            >
              <Save className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Saving...' : 'Save Notes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDetailPanel;